/**
 * insurerTpaRegisters — the fixed list of IRDAI-licensed TPAs and
 * IRDAI-registered general/standalone-health insurers shown in the
 * "Insurer name" / "TPA name" comboboxes on `PayerInsuranceCard`
 * (TPA-PREAUTH/tpa-bill FIX-8, owner ask: "add common most famous Insurer
 * and TPA in the dropdown, based upon facts from Indian market only, no
 * guess").
 *
 * SOURCE OF TRUTH: fetched LIVE from irdai.gov.in on the `as_of` date below.
 * No market-share ranking is asserted — both lists are alphabetical (insurers
 * split by type first: public_sector_general, then general, then
 * standalone_health, per IRDAI's own page grouping).
 *
 * Sources:
 *   - IRDAI - TPA (Third Party Administrators): https://irdai.gov.in/tpa
 *     ("List of Third Party Administrators as on 18 Feb, 2026")
 *   - IRDAI - List of General Insurers:
 *     https://irdai.gov.in/web/guest/list-of-general-insurers
 *     ("Last updated on September 07, 2026")
 *   - IRDAI - List of Health Insurers (Standalone Health Insurers):
 *     https://irdai.gov.in/list-of-health-insurers
 *     ("Last updated on September 07, 2026")
 *
 * Exclusions applied at the source: ECGC Limited (export credit insurer) and
 * Agriculture Insurance Company of India Limited (agricultural insurer) —
 * per brief. Life insurers/reinsurers were not on the fetched pages. Raksha
 * Health Insurance TPA merged into Medi Assist Insurance TPA effective 01 Jan
 * 2025 and is not listed separately (per the IRDAI TPA page).
 *
 * DO NOT add or edit an entry by hand — this is a straight copy of
 * `state/epics/TPA-PREAUTH/tpa-bill/insurer-tpa-registers.json`. Any change
 * to the register goes through that source-of-truth file first.
 */

export const REGISTER_AS_OF = '2026-09-19';

export const REGISTER_SOURCES = [
  { name: 'IRDAI - TPA (Third Party Administrators)', url: 'https://irdai.gov.in/tpa' },
  { name: 'IRDAI - List of General Insurers', url: 'https://irdai.gov.in/web/guest/list-of-general-insurers' },
  { name: 'IRDAI - List of Health Insurers (Standalone Health Insurers)', url: 'https://irdai.gov.in/list-of-health-insurers' },
];

/** 17 IRDAI-licensed TPAs, alphabetical by registered name. */
export const TPA_REGISTER = [
  { name: 'AKNA Health Insurance TPA Private Limited', irdai_registration_no: '039', display_name: 'AKNA Health' },
  { name: 'Ericson Insurance TPA Private Limited', irdai_registration_no: '35', display_name: 'Ericson' },
  { name: 'Family Health Plan Insurance TPA Limited', irdai_registration_no: '013', display_name: 'Family Health Plan' },
  { name: 'Genins India Insurance TPA Limited', irdai_registration_no: '020', display_name: 'Genins India' },
  { name: 'Good Health Insurance TPA Limited', irdai_registration_no: '023', display_name: 'Good Health' },
  { name: 'Health Assist Insurance TPA Pvt. Ltd', irdai_registration_no: '026', display_name: 'Health Assist' },
  { name: 'Health India Insurance TPA Services Private Limited', irdai_registration_no: '022', display_name: 'Health India' },
  { name: 'Health Insurance TPA of India Limited', irdai_registration_no: '36', display_name: 'Health Insurance TPA of India' },
  { name: 'Heritage Health Insurance TPA Private Limited', irdai_registration_no: '008', display_name: 'Heritage Health' },
  { name: 'Link-K Insurance TPA Private Limited', irdai_registration_no: '038', display_name: 'Link-K' },
  { name: 'MDIndia Health Insurance TPA Private Limited', irdai_registration_no: '005', display_name: 'MDIndia' },
  { name: 'Medi Assist Insurance TPA Private Limited', irdai_registration_no: '003', display_name: 'Medi Assist' },
  { name: 'Medsave Health Insurance TPA Limited', irdai_registration_no: '019', display_name: 'Medsave' },
  { name: 'Paramount Health Services & Insurance TPA Private Limited', irdai_registration_no: '006', display_name: 'Paramount' },
  { name: 'Park Mediclaim Insurance TPA Private Limited', irdai_registration_no: '025', display_name: 'Park Mediclaim' },
  { name: 'Vidal Health Insurance TPA Private Limited', irdai_registration_no: '016', display_name: 'Vidal Health' },
  { name: 'Volo Health Insurance TPA Pvt. Ltd', irdai_registration_no: '018', display_name: 'Volo Health' },
];

/**
 * 34 IRDAI-registered general/standalone-health insurers — 4
 * public_sector_general + 22 general + 8 standalone_health, alphabetical
 * within each type.
 */
export const INSURER_REGISTER = [
  { name: 'National Insurance Company Limited', type: 'public_sector_general', display_name: 'National Insurance' },
  { name: 'The New India Assurance Company Limited', type: 'public_sector_general', display_name: 'New India Assurance' },
  { name: 'The Oriental Insurance Company Limited', type: 'public_sector_general', display_name: 'Oriental Insurance' },
  { name: 'United India Insurance Company Limited', type: 'public_sector_general', display_name: 'United India Insurance' },

  { name: 'Acko General Insurance Limited', type: 'general', display_name: 'Acko' },
  { name: 'Bajaj General Insurance Limited', type: 'general', display_name: 'Bajaj General Insurance' },
  { name: 'Cholamandalam MS General Insurance Company Limited', type: 'general', display_name: 'Chola MS' },
  { name: 'Generali Central Insurance Company Limited', type: 'general', display_name: 'Generali Central' },
  { name: 'Go Digit General Insurance Limited', type: 'general', display_name: 'Go Digit' },
  { name: 'HDFC ERGO General Insurance Company Limited', type: 'general', display_name: 'HDFC ERGO' },
  { name: 'ICICI Lombard General Insurance Company Limited', type: 'general', display_name: 'ICICI Lombard' },
  { name: 'IFFCO TOKIO General Insurance Company Limited', type: 'general', display_name: 'IFFCO Tokio' },
  { name: 'IndusInd General Insurance Company Limited', type: 'general', display_name: 'IndusInd General Insurance' },
  { name: 'Kiwi General Insurance Limited', type: 'general', display_name: 'Kiwi' },
  { name: 'Kshema General Insurance Limited', type: 'general', display_name: 'Kshema' },
  { name: 'Liberty General Insurance Limited', type: 'general', display_name: 'Liberty General' },
  { name: 'Magma General Insurance Limited', type: 'general', display_name: 'Magma' },
  { name: 'Navi General Insurance Limited', type: 'general', display_name: 'Navi' },
  { name: 'Raheja QBE General Insurance Co. Ltd.', type: 'general', display_name: 'Raheja QBE' },
  { name: 'Royal Sundaram General Insurance Company Limited', type: 'general', display_name: 'Royal Sundaram' },
  { name: 'SBI General Insurance Company Limited', type: 'general', display_name: 'SBI General' },
  { name: 'Shriram General Insurance Company Limited', type: 'general', display_name: 'Shriram General' },
  { name: 'Tata AIG General Insurance Company Limited', type: 'general', display_name: 'Tata AIG' },
  { name: 'Universal Sompo General Insurance Company Limited', type: 'general', display_name: 'Universal Sompo' },
  { name: 'Zurich Kotak General Insurance Company (India) Limited', type: 'general', display_name: 'Zurich Kotak' },
  { name: 'Zuno General Insurance Ltd.', type: 'general', display_name: 'Zuno' },

  { name: 'Aditya Birla Health Insurance Co. Ltd.', type: 'standalone_health', display_name: 'Aditya Birla Health' },
  { name: 'Care Health Insurance Ltd.', type: 'standalone_health', display_name: 'Care Health' },
  { name: 'Galaxy Health Insurance Company Limited', type: 'standalone_health', display_name: 'Galaxy Health' },
  { name: 'Manipal Cigna Health Insurance Company Limited', type: 'standalone_health', display_name: 'ManipalCigna' },
  { name: 'Narayana Health Insurance Ltd.', type: 'standalone_health', display_name: 'Narayana Health' },
  { name: 'Niva Bupa Health Insurance Co Ltd.', type: 'standalone_health', display_name: 'Niva Bupa' },
  { name: 'Prudential HCL Health Insurance Limited', type: 'standalone_health', display_name: 'Prudential HCL Health' },
  { name: 'Star Health & Allied Insurance Co. Ltd.', type: 'standalone_health', display_name: 'Star Health' },
];
